import React, { useEffect, useMemo, useState } from 'react'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { Button, CustomInput } from 'reactstrap'
import styled from 'styled-components'
import { Check, X } from 'react-feather'

import { Show } from '~/components/commons'
import DateTimePicker from '~/components/DateTimePicker'
import Checkbox from '~/components/Checkbox'
import { timeToGoogle } from '~/utility/date'

function setTime(date) {
  const timeArray = date ? date.split(':') : ['00', '00']

  return {
    hours: parseInt(timeArray[0]),
    minutes: parseInt(timeArray[1]),
    seconds: 0,
    nanos: 0,
  }
}

function SpecialHoursRangeInput({
  id,
  day,
  defaultTimeState,
  aditionalTimeState,
}) {
  const [defaultHours, setDefaultHours] = defaultTimeState
  const [aditionalHours, setAditionalHours] = aditionalTimeState
  const [isFullTime, setIsFullTime] = useState(false)
  const [hasAditionalHours, setHasAditionalHours] = useState(false)

  const toggleIsFullTime = value => {
    if (value === isFullTime) return
    if (value) {
      setDefaultHours({
        ...defaultHours,
        openTime: setTime('00:00'),
        closeTime: setTime('23:59'),
      })
    }
    setIsFullTime(value)
  }

  useEffect(() => {
    toggleIsFullTime(
      day?.openTime?.hours === 0 &&
        day?.openTime?.minutes === 0 &&
        day?.closeTime?.hours === 23 &&
        day?.closeTime?.minutes === 59
    )

    // eslint-disable-next-line
  }, [day])

  const labelDate = useMemo(() => {
    const { startDate } = day

    if (!startDate) return ''

    return new Date(startDate?.year, startDate?.month - 1, startDate?.day)
  }, [day])

  return (
    <OpenRangeInputContainer>
      <span>
        <FormattedDate value={labelDate} />
      </span>
      <CustomInput
        id={`special-hours-switch-${id}`}
        bsSize="sm"
        type="switch"
        style={{ zIndex: '0 !important' }}
        checked={!day?.closed}
        onChange={e =>
          setDefaultHours({ ...defaultHours, closed: !e.target.checked })
        }
        inline
      >
        <span style={{ marginLeft: 5 }}>
          <FormattedMessage id={!day?.closed ? 'Open' : 'Closed'} />
        </span>
      </CustomInput>
      <Show condition={!day?.closed}>
        <OpenRangeDatesContainer>
          <AditionalDatesContainer>
            <DateTimePicker
              className="form-control"
              style={{ maxWidth: 80 }}
              value={`${defaultHours?.openTime?.hours}:${defaultHours?.openTime?.minutes}`}
              options={{
                enableTime: true,
                noCalendar: true,
                dateFormat: 'H:i',
              }}
              disabled={!!isFullTime}
              placeholder="Opens at"
              onChange={date => {
                setDefaultHours({
                  ...defaultHours,
                  openTime: setTime(timeToGoogle(date[0])),
                })
              }}
            />
            <span>-</span>
            <DateTimePicker
              className="form-control"
              style={{ maxWidth: 80 }}
              value={`${defaultHours?.closeTime?.hours}:${defaultHours?.closeTime?.minutes}`}
              options={{
                enableTime: true,
                noCalendar: true,
                dateFormat: 'H:i',
              }}
              disabled={!!isFullTime}
              placeholder="Closes at"
              onChange={date => {
                setDefaultHours({
                  ...defaultHours,
                  closeTime: setTime(timeToGoogle(date[0])),
                })
              }}
            />
          </AditionalDatesContainer>

          <Show condition={!hasAditionalHours}>
            <div className="d-flex">
              <Button
                disabled={!!isFullTime}
                style={{ padding: '0 10px', minWidth: 'fit-content' }}
                color="link"
                onClick={() => setHasAditionalHours(true)}
              >
                Add hours
              </Button>

              <Checkbox
                color="primary"
                icon={<Check className="vx-icon" size={10} />}
                label="24 Hours"
                size="sm"
                checked={isFullTime}
                onChange={e => toggleIsFullTime(e.target.checked)}
              />
            </div>
          </Show>

          <Show condition={!!hasAditionalHours}>
            <AditionalDatesContainer>
              <DateTimePicker
                className="form-control"
                style={{ maxWidth: 80 }}
                value={
                  aditionalHours.openTime
                    ? `${aditionalHours?.openTime?.hours}:${aditionalHours?.openTime?.minutes}`
                    : '12:00'
                }
                options={{
                  enableTime: true,
                  noCalendar: true,
                  dateFormat: 'H:i',
                }}
                placeholder="Opens at"
                onChange={date =>
                  setAditionalHours({
                    ...aditionalHours,
                    openTime: setTime(timeToGoogle(date[0])),
                  })
                }
              />
              <span>-</span>
              <DateTimePicker
                className="form-control"
                style={{ maxWidth: 80 }}
                value={
                  aditionalHours.closeTime
                    ? `${aditionalHours?.closeTime?.hours}:${aditionalHours?.closeTime?.minutes}`
                    : '12:00'
                }
                options={{
                  enableTime: true,
                  noCalendar: true,
                  dateFormat: 'H:i',
                }}
                placeholder="Closes at"
                onChange={date => {
                  setAditionalHours({
                    ...aditionalHours,
                    closeTime: setTime(timeToGoogle(date[0])),
                  })
                }}
              />
              <Button
                disabled={!!isFullTime}
                style={{ padding: '0' }}
                color="link"
                onClick={() => setHasAditionalHours(false)}
              >
                <X size={25} color="#8D8D8D" />
              </Button>
            </AditionalDatesContainer>
          </Show>
        </OpenRangeDatesContainer>
      </Show>
    </OpenRangeInputContainer>
  )
}

const OpenRangeInputContainer = styled.div`
  display: grid;
  grid-template-columns: 13% auto 1fr;
  grid-column-gap: 15px;
  align-items: center;
  margin-bottom: 10px;
  min-height: 38px;
`

const OpenRangeDatesContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`

const AditionalDatesContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`

export default React.memo(SpecialHoursRangeInput)
